import * as React from 'react';
import Box from '@mui/material/Box';
import { Avatar, Card, CardHeader, Container, Divider, Typography, SvgIcon } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

import type { Metadata } from '@/types/v2/metadata';
import { config } from '@/config';
import axios from '@/lib/v2/axios';
import { dayjs } from '@/lib/v2/dayjs';
import { toast } from '@/components/core/v2/toaster';
import { CalendarProvider } from '@/components/v2/calendar/calendar-context';
import { CalendarView } from '@/components/v2/calendar/calendar-view';
import type { ViewMode } from '@/components/v2/calendar/types';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { CalendarBlank as CalendarBlankIcon, Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr';

const metadata = { title: `Calendar | ${config.site.name}` } satisfies Metadata;

interface CreateEventParams {
  title: string;
  description: string;
  start_date: Date;
  end_date: Date;
  all_day: boolean;
  type?: string;
  color?: string;
  community_id?: number;
  created_by?: number;
}

export function CalendarV2Page(): React.JSX.Element {
  const { view = 'dayGridMonth' } = useExtractSearchParams();
  const queryClient = useQueryClient();

  const { data } = useQuery<Event[]>({
    queryKey: ['calendar-events'],
    queryFn: async () => {
      const response = await axios.get(`${config.api.base}/v2/calendar/calendar-events`);
      console.log('Raw server response:', response.data);

      // Transform server data to match frontend Event interface
      const transformedData = response.data.map((event: any) => ({
        id: event.id,
        title: event.title,
        description: event.description,
        start: new Date(event.start_date),
        end: new Date(event.end_date),
        allDay: event.all_day,
        priority: 'low' as const, // Default priority
        color: event.color || event.event_type?.color,
        type: event.event_type?.name,
      }));

      console.log('Transformed data:', transformedData);
      return transformedData;
    },
  });

  const createEventMutation = useMutation({
    mutationFn: async (params: CreateEventParams) => {
      const response = await axios.post(`${config.api.base}/v2/calendar/calendar-events`, params);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success('Event created successfully');
    },
    onError: (error) => {
      console.error('Failed to create event:', error);
      toast.error('Failed to create event');
    },
  });

  const updateEventMutation = useMutation({
    mutationFn: async ({ eventId, params }: { eventId: string; params: Partial<CreateEventParams> }) => {
      const response = await axios.put(`${config.api.base}/v2/calendar/calendar-events/${eventId}`, params);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success('Event updated successfully');
    },
    onError: (error) => {
      console.error('Failed to update event:', error);
      toast.error('Failed to update event');
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: string) => {
      await axios.delete(`${config.api.base}/v2/calendar/calendar-events/${eventId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success('Event deleted successfully');
    },
    onError: (error) => {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    },
  });

  const handleCreateEvent = React.useCallback(
    (params: {
      title: string;
      description?: string;
      start: Date;
      end: Date;
      allDay: boolean;
      priority?: string;
      type?: string;
    }) => {
      // Ensure description is provided as server requires it
      const description = params.description?.trim() || '';

      if (!description) {
        toast.error('Description is required');
        return;
      }

      createEventMutation.mutate({
        title: params.title.trim(),
        description,
        start_date: params.start,
        end_date: params.end,
        all_day: params.allDay,
        ...(params.type?.trim() ? { type: params.type.trim() } : {}),
        // community_id and created_by will be handled by the server
      });
    },
    [createEventMutation]
  );

  const handleUpdateEvent = React.useCallback(
    (
      eventId: string,
      params: {
        title?: string;
        description?: string;
        start?: Date;
        end?: Date;
        allDay?: boolean;
        priority?: string;
        type?: string;
      }
    ) => {
      const updateParams: Partial<CreateEventParams> = {};

      if (params.title) updateParams.title = params.title.trim();
      if (params.description !== undefined) {
        const description = params.description.trim();
        if (!description) {
          toast.error('Description is required');
          return;
        }
        updateParams.description = description;
      }
      if (params.start) updateParams.start_date = params.start;
      if (params.end) updateParams.end_date = params.end;
      if (params.allDay !== undefined) updateParams.all_day = params.allDay;
      if (params.type?.trim()) updateParams.type = params.type.trim();

      updateEventMutation.mutate({
        eventId,
        params: updateParams,
      });
    },
    [updateEventMutation]
  );

  const handleDeleteEvent = React.useCallback(
    (eventId: string) => {
      deleteEventMutation.mutate(eventId);
    },
    [deleteEventMutation]
  );

  return (
    <React.Fragment>
      <Helmet>
        <title>{metadata.title}</title>
      </Helmet>
      <Box component="main" sx={{ flexGrow: 1, py: 3, backgroundColor: (theme) => theme.palette.background.default, minHeight: '100vh' }}>
        <Container maxWidth="xl">
          <CommunityPageHeader
            icon={<CalendarBlankIcon size={36} />}
            subtitle="View and manage your community events"
            title="Calendar"
          />

          <Card sx={{ mt: 4 }}>
            <CardHeader
              avatar={
                <Avatar>
                  <CalendarBlankIcon fontSize="var(--Icon-fontSize)" />
                </Avatar>
              }
              title={
                <Box alignItems="center" display="flex" gap={2} justifyContent="space-between">
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    Calendar
                  </Typography>
                  {/* Optional actions can be added here */}
                </Box>
              }
              titleTypographyProps={{ variant: 'h6', sx: { fontWeight: 600 } }}
            />
            <Divider />

            <Box sx={{ p: 2 }}>
              <CalendarProvider>
                <CalendarView view={view} />
              </CalendarProvider>
            </Box>
          </Card>
        </Container>
      </Box>
    </React.Fragment>
  );
}

function useExtractSearchParams(): { view?: ViewMode } {
  const [searchParams] = useSearchParams();

  return { view: (searchParams.get('view') || undefined) as ViewMode | undefined };
}
